import '@beyondtrust/btux-iconography-web';
import { Task } from '@lit/task';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { when } from 'lit/directives/when.js';
import './components/menu/defaults';
import { LANGUAGE_SELECTOR_FLAG, PLATFORM_HOME_PRODUCT_MENU_ITEM } from './constants';
import {
  createLanguagePickerAvailabilityChangeEvent,
  UPDATE_SELECTED_SITE_ACTION,
  UpdateSelectedSiteEvent,
} from './context';
import { computeBreadcrumbTrail, resolveCurrentProduct, resolveProductMenuItems } from './selectors/menu-selectors';
import styles from './styles.scss';
import {
  ADMIN_SLUG,
  AI_COWORKERS_SLUG,
  ENTITLE_SLUG,
  EPML_SLUG,
  FeatureFlagsConfig,
  INSIGHTS_SLUG,
  PASSWORD_SAFE_SLUG,
  PLATFORM_SLUG,
  PMC_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ProductMenuItem,
  ProductSlug,
  REMOTE_SUPPORT_SLUG,
  RenderData,
  SECRETS_SLUG,
  SiteData,
} from './types';
// importing here for use in app menus
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
// child custom elements
import { configureProfileCardLocalization } from '@beyondtrust/platform-profile-card';
import { ContextProvider } from '@lit/context';
import { configureLocalization, localized } from '@lit/localize';
import './components/overlay-menu-navigation-shell/overlay-menu-navigation-shell';
import './components/sidebar-menu-navigation-shell/sidebar-menu-navigation-shell';
import './components/breadcrumbs/breadcrumbs';
export { MENU_SIDEBAR_TAG, PlatformMenuSidebar } from './components/menu-sidebar/menu-sidebar';
export { NAV_SECTION_TAG, PlatformNavSection } from './components/nav-section/nav-section';
export { BREADCRUMBS_TAG, PlatformBreadcrumbs } from './components/breadcrumbs/breadcrumbs';
export { BREADCRUMB_ITEM_TAG, PlatformBreadcrumbItem } from './components/breadcrumbs/breadcrumb-item';
export { PLATFORM_TAG_TAG, PlatformTag } from './components/tag/tag';
import { dirtyContext } from './context/dirty-form';
import { menuDataContext, menuErrorContext } from './context/menu-data';
import { sitesDataContext } from './context/sites-data';
import { APIMenuDataController } from './controllers/api-menu-data-controller';
import { InactivityController } from './controllers/inactivity-controller';
import { LaunchDarklyController } from './controllers/launch-darkly-controller';
import { SitesDataController } from './controllers/sites-data-controller';
import { getEnvironmentLabel } from './environment';
import { flagContext } from './feature-flags';
import { sourceLocale, targetLocales } from './generated/locale-codes';
import { getFeatureFlagsConfig, getSiteData, selectSite } from './services/menu-data-store';
import { loadRuntimeTranslations, t } from './services/runtime-i18n';

export const { getLocale, setLocale } = configureLocalization({
  sourceLocale,
  targetLocales,
  loadLocale: (locale) => {
    return import(`./generated/locales/${locale}.js`);
  },
});

let profileCard: ReturnType<typeof configureProfileCardLocalization> | null = null;

async function applyLocale(locale: string) {
  await loadRuntimeTranslations(locale);
  await setLocale(locale);
  try {
    if (profileCard) {
      await profileCard.setLocale(locale);
    }
  } catch {
    // Profile card may not support all locales spog-elements does
  }
}

const EN_FALLBACK = 'en';

const resolveLocaleExact = (locale: string): string => {
  if (targetLocales.find((l) => l === locale) != undefined) {
    return locale;
  }
  return EN_FALLBACK;
};

const resolveLocaleBrowserDefault = (): string => {
  const lang = navigator.language;
  if (targetLocales.find((l) => l === lang) != undefined) {
    return lang;
  }
  const prefix = lang.split('-')[0];
  if (targetLocales.find((l) => l === prefix) != undefined) {
    return prefix;
  }
  return EN_FALLBACK;
};

let explicitLocale: string | null = null;

const getLanguageCookie = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)x-pathfinder-language=([^;]*)/);
  return match?.[1] || null;
};

(async () => {
  const cookieLocale = getLanguageCookie();
  if (cookieLocale && cookieLocale !== 'browserDefault') {
    explicitLocale = cookieLocale;
    await applyLocale(resolveLocaleExact(cookieLocale));
  } else {
    await applyLocale(resolveLocaleBrowserDefault());
  }
  profileCard = configureProfileCardLocalization();
  try {
    await profileCard.setLocale(getLocale());
  } catch {
    // Profile card may not support this locale
  }
})();

window.onlanguagechange = async () => {
  if (explicitLocale != null) {
    return;
  }
  await applyLocale(resolveLocaleBrowserDefault());
};

window.addEventListener('UPDATE_SELECTED_LANGUAGE_ACTION', async (event: Event) => {
  const { selectedLanguage } = (event as CustomEvent<{ selectedLanguage: string }>).detail;
  if (selectedLanguage === 'browserDefault') {
    explicitLocale = null;
    await applyLocale(resolveLocaleBrowserDefault());
  } else {
    explicitLocale = selectedLanguage;
    await applyLocale(resolveLocaleExact(selectedLanguage));
  }
});

/**
 * Tag name for the platform header.
 *
 * @public
 */
export const PLATFORM_HEADER_TAG = 'platform-header' as const;

/** @public */
export type NavigationLayout = 'sidebar' | 'overlay';

/**
 * Platform Menu web custom element.
 *
 * @public
 */
@customElement(PLATFORM_HEADER_TAG)
@localized()
export class PlatformMenu extends LitElement {
  @property({ reflect: true })
  public override role = 'banner';

  @property()
  activeRoute?: string;

  /** Opt in to breadcrumbs in the sidebar header's product region. @public */
  @property({ type: Boolean })
  breadcrumbs = false;

  /** Product opt-in to the sidebar layout; defaults to the overlay menu when unset. @public */
  @property({ attribute: 'navigation-layout' })
  navigationLayout?: NavigationLayout;

  @property({
    type: Boolean,
    converter: {
      fromAttribute: (value) => {
        if (value === '' || value === 'false' || value === '0') {
          return false;
        }
        return true;
      },
    },
  })
  isDirty = false;

  private _flagProvider = new ContextProvider(this, { context: flagContext });

  private _menuDataProvider = new ContextProvider(this, { context: menuDataContext });

  private _menuErrorProvider = new ContextProvider(this, { context: menuErrorContext });

  private _dirtyProvider = new ContextProvider(this, { context: dirtyContext });

  private _sitesDataProvider = new ContextProvider(this, { context: sitesDataContext });

  private launchDarklyController = new LaunchDarklyController(this);

  private apiMenuDataController = new APIMenuDataController(this);

  private sitesDataController = new SitesDataController(this);

  private _lastPublishedAvailable?: boolean;

  /**
   * Whether the platform language picker is available. `undefined` means not yet resolved,
   * not "no", so consumers must act only on a boolean. Mirrors the profile card's own gate
   * (spog-profile-card, `user-profile-card.ts`); keep the two in step. @public
   */
  public get languagePickerAvailable(): boolean | undefined {
    if (!this.launchDarklyController.flagsResolved) {
      return undefined;
    }
    return this.launchDarklyController.flags[LANGUAGE_SELECTOR_FLAG] === true;
  }

  static override styles = [styles];

  private _renderTask?: Task<[SiteData | null | undefined], RenderData | null>;
  private _siteData?: Task<[], SiteData | null>;
  private _flagConfig?: Task<[], FeatureFlagsConfig | null>;

  override connectedCallback(): void {
    super.connectedCallback();

    // Monitors user activity and redirects to /session/logout
    // after 20 minutes of inactivity. Self-registering via addController().
    const inactivityController = new InactivityController(this);

    // Pipe flags to the inactivity controller so it can gate on
    // the `platform-inactivity` feature flag.
    new Task(this, {
      task: async ([f]) => {
        inactivityController.flags = f ?? {};
      },
      args: () => [this.launchDarklyController.flags],
    });

    // INFO(kohearon): Any changes to the user profile
    // passed through configure feature flags.
    new Task(
      this,
      ([userinfo]) => {
        if (!userinfo) {
          return;
        }

        this.launchDarklyController.userinfo = userinfo;
      },
      () => [this.sitesDataController.userInfo] as const,
    );

    this._siteData = new Task(this, {
      task: async (): Promise<SiteData | null> => {
        return await getSiteData();
      },
      args: () => [],
    });

    this._flagConfig = new Task(this, {
      task: async (): Promise<FeatureFlagsConfig | null> => {
        return await getFeatureFlagsConfig();
      },
      args: () => [],
    });

    new Task(this, {
      task: async ([f]) => {
        if (f == undefined) {
          return;
        }
        this.launchDarklyController.initializeClient(f.platformClientID, f.baseURI, f.streamURI, f.eventsURI);
      },
      args: () => [this._flagConfig?.value],
    });

    // Pipe flags to Flag ContextProvider since LDClient does not neatly trigger tasks directly
    new Task(this, {
      task: async ([f]) => {
        this._flagProvider.setValue(f ?? {});
        if (this.launchDarklyController.flagsResolved && f && f[LANGUAGE_SELECTOR_FLAG] === false) {
          document.cookie = 'x-pathfinder-language=; Max-Age=0; path=/';
          window.dispatchEvent(
            new CustomEvent('UPDATE_SELECTED_LANGUAGE_ACTION', {
              detail: { selectedLanguage: 'en' },
            }),
          );
        }
      },
      args: () => [this.launchDarklyController.flags],
    });

    // Separate from the flag Task above, which owns the kill switch and must keep testing for
    // an explicit `false`. Imperative setAttribute rather than a reflected @property so the
    // attribute stays read-only to consumers and cannot be spoofed.
    new Task(this, {
      task: async ([available]) => {
        if (available === undefined) {
          return; // not yet resolved: reflect nothing, publish nothing
        }
        this.setAttribute('data-language-picker', available ? 'available' : 'unavailable');
        if (available === this._lastPublishedAvailable) {
          return;
        }
        this._lastPublishedAvailable = available;
        this.dispatchEvent(createLanguagePickerAvailabilityChangeEvent(available));
      },
      args: () => [this.languagePickerAvailable],
    });

    // Pipe api menu data to ContextProvider
    new Task(this, {
      task: async ([data]) => {
        if (data == undefined || Object.keys(data).length === 0) {
          return;
        }
        this._menuDataProvider.setValue(data ?? {});
      },
      args: () => [this.apiMenuDataController.menuData],
    });

    // Pipe api menu failures to ContextProvider
    new Task(this, {
      task: async ([errors]) => {
        if (errors == undefined || Object.keys(errors).length === 0) {
          return;
        }
        this._menuErrorProvider.setValue(errors);
      },
      args: () => [this.apiMenuDataController.menuErrors],
    });

    // Pipe site data to ContextProvider
    new Task(this, {
      task: async ([data]) => {
        if (data == undefined || Object.keys(data).length === 0) {
          return;
        }
        this._sitesDataProvider.setValue(data ?? []);
      },
      args: () => [this.sitesDataController.sitesData],
    });

    new Task(this, {
      task: ([dirty]) => {
        this._dirtyProvider.setValue(dirty);
      },
      args: () => [this.isDirty],
    });

    this._renderTask = new Task(this, {
      task: async ([s]): Promise<RenderData | null> => {
        if (s == undefined) {
          return null;
        }
        const site = s as SiteData;
        const productMenuItems = resolveProductMenuItems(site);
        const currentProduct = resolveCurrentProduct(productMenuItems, window.location.pathname);

        // Fetch every product's menu — including the active one — so cross-product UI like the
        // reporting menu always has each product's data in context.
        const apiProducts = productMenuItems.map((product) => product.slug);
        this.apiMenuDataController.availableProducts = apiProducts;

        return {
          site,
          productMenuItems,
          currentProduct,
        } as RenderData;
      },
      args: () => [this._siteData?.value] as const,
    });
    this.updateComplete.then(() => {
      this.shadowRoot?.addEventListener(UPDATE_SELECTED_SITE_ACTION, this._handleSiteSelected.bind(this));
    });
  }

  override disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener(UPDATE_SELECTED_SITE_ACTION, this._handleSiteSelected.bind(this));
  }

  private async _handleSiteSelected(event: Event) {
    const {
      detail: { selectedSiteId },
    } = event as UpdateSelectedSiteEvent;
    await selectSite(selectedSiteId);
  }

  private get isSidebarNavigationActive(): boolean {
    return this.navigationLayout === 'sidebar';
  }

  /**
   * Forwarding slots for the consumer product-menu replacement contract.
   *
   * Consumer content lives as light DOM of `platform-header`; each shadow layer between it
   * and `platform-menu` must re-expose the named slots or the content is silently dropped
   * (this regressed when the shells were extracted). The default menus stay HERE, in the
   * outermost forwarding layer — nested-slot flattening never falls back to an inner slot's
   * default once a forwarding slot is assigned, so defaults in the shells would be
   * suppressed for every consumer that slots nothing.
   */
  private renderProductMenuSlots(availableProducts: ProductSlug[]): TemplateResult<1> {
    return html`
      <slot name="admin-menu" slot="admin-menu">
        ${when(
          availableProducts.includes(ADMIN_SLUG),
          () => html`<platform-api-menu product=${ADMIN_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>
      <slot name="platform-menu" slot="platform-menu">
        ${when(
          availableProducts.includes(PLATFORM_SLUG),
          () => html`<platform-default-platform-menu></platform-default-platform-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="insights-menu" slot="insights-menu">
        ${when(
          availableProducts.includes(INSIGHTS_SLUG),
          () => html`<platform-api-menu product=${INSIGHTS_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="epml-menu" slot="epml-menu">
        ${when(
          availableProducts.includes(EPML_SLUG),
          () => html`<platform-api-menu product=${EPML_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="pmc-menu" slot="pmc-menu">
        ${when(
          availableProducts.includes(PMC_SLUG),
          () => html`<platform-api-menu product=${PMC_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="passwordsafe-menu" slot="passwordsafe-menu">
        ${when(
          availableProducts.includes(PASSWORD_SAFE_SLUG),
          () => html`<platform-api-menu product=${PASSWORD_SAFE_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="privileged-remote-access-menu" slot="privileged-remote-access-menu">
        ${when(
          availableProducts.includes(PRIVILEGED_REMOTE_ACCESS_SLUG),
          () => html`<platform-api-menu product=${PRIVILEGED_REMOTE_ACCESS_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="remote-support-menu" slot="remote-support-menu">
        ${when(
          availableProducts.includes(REMOTE_SUPPORT_SLUG),
          () => html`<platform-api-menu product=${REMOTE_SUPPORT_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="secrets-menu" slot="secrets-menu">
        ${when(
          availableProducts.includes(SECRETS_SLUG),
          () => html`<platform-secrets-menu></platform-secrets-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="entitle-menu" slot="entitle-menu">
        ${when(
          availableProducts.includes(ENTITLE_SLUG),
          () => html`<platform-api-menu product=${ENTITLE_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="ai-coworkers-menu" slot="ai-coworkers-menu">
        ${when(
          availableProducts.includes(AI_COWORKERS_SLUG),
          () => html`<platform-api-menu product=${AI_COWORKERS_SLUG}></platform-api-menu>`,
          () => nothing,
        )}
      </slot>

      <slot name="reporting-insights-menu" slot="reporting-insights-menu">
        ${when(
          availableProducts.includes(INSIGHTS_SLUG),
          () => html`<platform-reporting-insights-menu></platform-reporting-insights-menu>`,
          () => nothing,
        )}
      </slot>
    `;
  }

  /**
   * Build the inferred breadcrumb trail from the current product's menu data,
   * rendered as the breadcrumbs slot's default (used when the consumer opts in
   * but slots nothing). Falls back to today's single label — the active route or
   * the product name — when no menu leaf matches the current path.
   *
   * Scope note: inference reads the API-driven menu context (`_menuDataProvider`),
   * so products whose menus come only from `STATIC_PRODUCT_MENUS` (e.g. Admin,
   * Secrets) currently fall back to the single-label crumb.
   */
  private renderInferredBreadcrumbs(currentProduct: ProductMenuItem): TemplateResult<1> {
    const trail = computeBreadcrumbTrail(
      currentProduct,
      this._menuDataProvider.value?.[currentProduct.slug],
      window.location.pathname,
    );
    const crumbs = trail.length > 0 ? trail : [{ name: this.activeRoute || currentProduct.name, current: true }];

    return html`
      <platform-breadcrumbs>
        ${repeat(
          crumbs,
          // Key by identity so navigating between sibling pages renders a fresh crumb
          // element (with an up-to-date label/tooltip) instead of reusing the previous
          // element and only patching its light-DOM text.
          (crumb) => `${crumb.id ?? ''}:${crumb.name}`,
          (crumb) => html`
            <platform-breadcrumb-item ?current=${crumb.current} href=${ifDefined(crumb.href)}>
              ${t(crumb.id, crumb.name)}
            </platform-breadcrumb-item>
          `,
        )}
      </platform-breadcrumbs>
    `;
  }

  override render() {
    return html`${this._renderTask?.render({
      complete: (value) => {
        const { productMenuItems, currentProduct, site } = value ?? {
          productMenuItems: [PLATFORM_HOME_PRODUCT_MENU_ITEM],
          currentProduct: PLATFORM_HOME_PRODUCT_MENU_ITEM,
          site: {
            external_id: '',
            name: '',
            products: [],
            residency: 'US',
            site_id: '',
            slug: '',
          },
        };

        const availableProducts = productMenuItems.map((p) => p.slug);
        const environmentLabel = getEnvironmentLabel();

        return this.isSidebarNavigationActive
          ? html`
              <platform-sidebar-menu-navigation-shell
                .productMenuItems=${productMenuItems}
                .currentProduct=${currentProduct}
                .site=${site}
                .activeRoute=${this.activeRoute ?? ''}
                .environmentLabel=${environmentLabel}
              >
                ${when(
                  this.breadcrumbs,
                  () =>
                    html`<slot name="breadcrumbs" slot="breadcrumbs"
                      >${this.renderInferredBreadcrumbs(currentProduct)}</slot
                    >`,
                )}
                ${this.renderProductMenuSlots(availableProducts)}
              </platform-sidebar-menu-navigation-shell>
            `
          : html`
              <platform-overlay-menu-navigation-shell
                .productMenuItems=${productMenuItems}
                .currentProduct=${currentProduct}
                .site=${site}
                .activeRoute=${this.activeRoute ?? ''}
                .environmentLabel=${environmentLabel}
              >
                ${this.renderProductMenuSlots(availableProducts)}
              </platform-overlay-menu-navigation-shell>
            `;
      },
    })}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [PLATFORM_HEADER_TAG]: PlatformMenu;
  }
}
